import React, { useState } from 'react';
import { X } from 'lucide-react';
import { config } from '../../../config';
import './SocialPaymentModal.css';

interface SocialPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SocialPaymentModal({ isOpen, onClose }: SocialPaymentModalProps) {
  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (!name) {
      setError('Name is required');
      return;
    }

    // Default email since the form no longer asks for it
    const defaultEmail = 'guest@luminar.com';

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${config.API_BASE_URL}/tickets/initiate-payglocal-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticketType: 'normal',
          eventType: 'social',
          quantity: 1,
          pricePerTicket: Number(amount),
          buyerName: name,
          buyerEmail: defaultEmail,
          buyerPhone: phone,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Payment initiation failed');
      
      // Mock mode fallback if credentials are not set on server
      if (data.mockMode) {
        const mockRes = await fetch(`${config.API_BASE_URL}/tickets/confirm-mock-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ticketType: 'normal',
            eventType: 'social',
            quantity: 1,
            pricePerTicket: Number(amount),
            buyerName: name,
            buyerEmail: defaultEmail,
            buyerPhone: phone,
          }),
        });
        if (!mockRes.ok) throw new Error('Ticket creation failed');
        const created = await mockRes.json();
        const ticketIds = created.map((t: any) => t.id).join(',');
        window.location.href = `/payment-success?ticketIds=${ticketIds}&buyerName=${encodeURIComponent(name)}&buyerEmail=${encodeURIComponent(defaultEmail)}&buyerPhone=${encodeURIComponent(phone)}&totalAmount=${amount}`;
        return;
      }

      if (data.payglobalRedirectUrl) {
        window.location.href = data.payglobalRedirectUrl;
      } else {
        throw new Error('No redirect URL received');
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
      setLoading(false);
    }
  };

  return (
    <div className="social-modal-overlay" onClick={onClose}>
      <div className="social-modal" onClick={e => e.stopPropagation()}>
        <button className="social-modal__close" onClick={onClose}>
          <X size={24} />
        </button>
        
        <h2 className="social-modal__title">Social Contribution</h2>
        <p className="social-modal__desc">
          Enter an amount to proceed with your payment securely.
        </p>

        <form className="social-modal__form" onSubmit={handleSubmit}>
          <div className="social-modal__group">
            <label htmlFor="amount">Amount ($)</label>
            <input
              id="amount"
              type="number"
              placeholder="e.g. 1000"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              required
              min="1"
            />
          </div>
          
          <div className="social-modal__group">
            <label htmlFor="name">Full Name</label>
            <input
              id="name"
              type="text"
              placeholder="John Doe"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </div>

          <div className="social-modal__group">
            <label htmlFor="phone">Phone Number (Optional)</label>
            <input
              id="phone"
              type="tel"
              placeholder="+91 9876543210"
              value={phone}
              onChange={e => setPhone(e.target.value)}
            />
          </div>

          {error && <div style={{ color: '#ff4444', fontSize: '0.9rem' }}>{error}</div>}

          <button type="submit" className="social-modal__submit" disabled={loading}>
            {loading ? 'Processing...' : 'Pay Now'}
          </button>
        </form>
      </div>
    </div>
  );
}
